"use client";

import { BlurFade } from "@/components/ui/blur-fade";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface AccordionItem {
  title: string;
  content: string;
}

interface AccordionSectionProps {
  title: string;
  subtitle?: string;
  items: AccordionItem[];
}

const AccordionSection = ({ title, subtitle, items }: AccordionSectionProps) => {
  if (!items || items.length === 0) return null;

  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-3xl mx-auto flex flex-col items-center gap-12">
        {/* Header */}
        <div className="text-center space-y-4">
          <BlurFade delay={0.1}>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              {title}
            </h2>
          </BlurFade>
          {subtitle && (
            <BlurFade delay={0.15}>
              <p className="text-foreground/60 text-lg max-w-xl mx-auto">
                {subtitle}
              </p>
            </BlurFade>
          )}
        </div>

        {/* Accordion */}
        <BlurFade delay={0.2} className="w-full">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {items.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border border-foreground/5 rounded-2xl px-6 data-[state=open]:bg-muted/50 transition-colors"
              >
                <AccordionTrigger className="text-foreground font-semibold hover:no-underline py-5 text-left">
                  {item.title}
                </AccordionTrigger>
                <AccordionContent className="text-foreground/60 leading-relaxed pb-5">
                  {item.content}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </BlurFade>
      </div>
    </section>
  );
};

export default AccordionSection;
